import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardHeader,
  DateInput,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Field,
  Flex,
  Input,
  Spinner,
  Switch,
  Text,
  Textarea,
  toaster,
  Tooltip,
} from '@optiaxiom/react';
import { IconPlay, IconTrashCan, IconXmark } from '@optimizely/axiom-icons';

import { ArtifactContainer, ArtifactProvider, Artifacts } from '@frontend/components/Artifact';
import {
  useExecuteSpecializedAgent,
  useGetAgentMemoryChunks,
  useGetSpecializedAgentExecution,
} from '@frontend/features/agents/hooks';
import { uploadFile } from '@frontend/features/agents/services/specialized-agent';
import { useInstanceId, useOpalUser } from '@frontend/hooks';
import {
  AgentExecutionResponse,
  AgentParameter,
  AgentParameterType,
  AgentParameterValue,
  SpecializedAgent,
} from '@frontend/types/specialized-agent';

import { StopExecutionButton } from '../../shared/StopExecutionButton';
import { EmptyStateMessage } from '../shared/EmptyStateMessage';
import { ExecutionDetailsCard } from '../shared/ExecutionDetailsCard';
import { ExecutionOutputDisplay } from '../shared/ExecutionOutputDisplay';
import { MemoryChunksDisplay } from '../shared/MemoryChunksDisplay';

type AgentExecutionFormProps = {
  agent?: SpecializedAgent;
  hideRightSidebar: () => void;
};

type ParameterValues = Record<string, AgentParameterValue<AgentParameterType>>;

export function AgentExecutionForm({ agent, hideRightSidebar }: AgentExecutionFormProps) {
  const [parameterValues, setParameterValues] = useState<ParameterValues>({});
  const [executionResponse, setExecutionResponse] = useState<AgentExecutionResponse | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const [isUploadingFile, setIsUploadingFile] = useState<boolean>(false);
  const { opalUser } = useOpalUser();
  const { instanceId } = useInstanceId();

  const { mutate: executeAgent, isPending: isExecuting } = useExecuteSpecializedAgent({
    onSuccess: data => {
      setExecutionResponse(data);
      toaster.create('Agent execution started', { type: 'information' });
    },
    onError: () => {
      handleExecuteAgentFailure();
    },
  });

  const handleExecuteAgentSuccess = useCallback(() => {
    toaster.create('Agent executed successfully', { type: 'success' });
  }, []);

  const handleExecuteAgentFailure = useCallback(() => {
    toaster.create('Agent execution failed', { type: 'danger' });
  }, []);

  const { execution } = useGetSpecializedAgentExecution(
    instanceId ?? '',
    executionResponse?.id ?? '',
    handleExecuteAgentSuccess,
    handleExecuteAgentFailure
  );

  const isStreaming = execution
    ? execution?.status.toLowerCase() !== 'completed' &&
      execution?.status.toLowerCase() !== 'failed'
    : false;
  const { memoryChunks } = useGetAgentMemoryChunks(
    execution?.memory_id,
    execution?.links?.memory,
    isStreaming
  );

  const parameters = useMemo(() => agent?.parameters || [], [agent?.parameters]);

  const handleParameterChange = <T extends AgentParameterType>(
    paramName: string,
    value: AgentParameterValue<T>
  ) => {
    setParameterValues(prev => ({
      ...prev,
      [paramName]: value,
    }));
  };

  const handleFileUpload = async (paramName: string, file: File) => {
    if (!file) return;

    setUploadingFiles(prev => [...prev, paramName]);
    setIsUploadingFile(true);

    try {
      const uploaded = await uploadFile(
        file,
        opalUser.instance.customer_id,
        opalUser.instance.instance_id
      );

      if (uploaded) {
        // Structure the file parameter as expected by the backend
        const fileParam = {
          url: uploaded.url,
          type: uploaded.type,
        };
        handleParameterChange(paramName, fileParam as AgentParameterValue<AgentParameterType>);
      }
    } catch (error) {
      console.error('File upload failed:', error);
    } finally {
      setUploadingFiles(prev => {
        const newUploadingFiles = prev.filter(name => name !== paramName);
        // Update isUploadingFile based on whether there are still files uploading
        setIsUploadingFile(newUploadingFiles.length > 0);
        return newUploadingFiles;
      });
    }
  };

  const areRequiredParametersFilled = useCallback(() => {
    const missingParams = parameters
      .filter(param => param.required)
      .filter(param => {
        const value = parameterValues[param.name];

        switch (param.type) {
          case 'string':
            return !value || (typeof value === 'string' && value.trim() === '');
          case 'number':
            return value === null || value === undefined || Number.isNaN(value);
          case 'boolean':
            return false; // boolean is always valid (true/false)
          case 'file':
            return !value || (typeof value === 'object' && !value.url);
          case 'object':
            return !value || (typeof value === 'string' && value.trim() === '');
          case 'datetime':
            return !value || (typeof value === 'string' && value.trim() === '');
          default:
            return !value;
        }
      });

    return { isValid: missingParams.length === 0, missingParams };
  }, [parameters, parameterValues]);

  const getTooltipContent = useCallback(
    (hasParameters: boolean = true) => {
      if (isUploadingFile) return 'Uploading files...';
      if (isExecuting) return 'Agent is running...';

      if (hasParameters) {
        const { isValid, missingParams } = areRequiredParametersFilled();
        if (!isValid) {
          return `Please fill in required parameters: ${missingParams.map(p => p.name).join(', ')}`;
        }
      }

      return '';
    },
    [isUploadingFile, isExecuting, areRequiredParametersFilled]
  );

  const tooltipContentWithParams = useMemo(() => getTooltipContent(true), [getTooltipContent]);

  const tooltipContentWithoutParams = useMemo(() => getTooltipContent(false), [getTooltipContent]);
  const isRunAgentButtonDisabled = useMemo(() => {
    const { isValid } = areRequiredParametersFilled();
    return !isValid || isExecuting || isUploadingFile;
  }, [areRequiredParametersFilled, isExecuting, isUploadingFile]);

  const handleExecuteAgent = async () => {
    if (!agent || !agent.links.execute) return;

    setExecutionResponse(null);
    executeAgent({
      executeUrl: agent.links.execute,
      parameters: parameterValues,
    });
  };

  const renderParameterInput = (parameter: AgentParameter<AgentParameterType>) => {
    const value = parameterValues[parameter.name] || '';

    switch (parameter.type) {
      case 'string':
        return (
          <Textarea
            key={parameter.name}
            value={value as string}
            resize="vertical"
            rows={2}
            placeholder={parameter.description || `Enter ${parameter.name}`}
            onChange={e => handleParameterChange(parameter.name, e.target.value)}
          />
        );
      case 'number':
        return (
          <Input
            key={parameter.name}
            type="number"
            value={value as number}
            placeholder={parameter.description || `Enter ${parameter.name}`}
            onChange={e => handleParameterChange(parameter.name, Number(e.target.value))}
          />
        );
      case 'boolean':
        return (
          <Switch
            key={parameter.name}
            checked={value as boolean}
            onCheckedChange={checked => handleParameterChange(parameter.name, checked)}
          >
            {parameter.description || parameter.name}
          </Switch>
        );
      case 'datetime':
        return (
          <DateInput
            key={parameter.name}
            type="datetime-local"
            value={value as string}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleParameterChange(parameter.name, e.target.value)
            }
          />
        );
      case 'object':
        return (
          <Textarea
            key={parameter.name}
            value={typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
            placeholder={parameter.description || `Enter ${parameter.name} (JSON format)`}
            onChange={e => {
              try {
                const parsedValue = JSON.parse(e.target.value);
                handleParameterChange(parameter.name, parsedValue);
              } catch {
                handleParameterChange(parameter.name, e.target.value);
              }
            }}
            rows={3}
          />
        );
      case 'file': {
        const currentFileValue = value as { url?: string; type?: string } | undefined;
        const isUploading = uploadingFiles.includes(parameter.name);

        return (
          <Flex key={parameter.name} flexDirection="column" gap="8">
            {currentFileValue?.url && (
              <Box border="1" borderColor="border.default" rounded="md" p="12">
                <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
                  <Flex flexDirection="column" gap="4">
                    <Text fontSize="sm" fontWeight="500">
                      {currentFileValue.url.split('/').pop() || 'Uploaded file'}
                    </Text>
                    <Text fontSize="sm" color="fg.secondary">
                      {(currentFileValue.type || 'Unknown type').toUpperCase()}
                    </Text>
                  </Flex>
                  <Button
                    appearance="subtle"
                    size="sm"
                    icon={<IconTrashCan size="16" />}
                    onClick={() =>
                      handleParameterChange(
                        parameter.name,
                        '' as AgentParameterValue<AgentParameterType>
                      )
                    }
                    aria-label="Remove file"
                  />
                </Flex>
              </Box>
            )}
            <Input
              type="file"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) {
                  handleFileUpload(parameter.name, file);
                }
              }}
              disabled={isUploading}
            />
            {isUploading && (
              <Flex alignItems="center" gap="8">
                <Spinner size="sm" />
                <Text fontSize="sm" color="fg.secondary">
                  Uploading...
                </Text>
              </Flex>
            )}
          </Flex>
        );
      }
      default:
        return (
          <Input
            key={parameter.name}
            value={value as string}
            placeholder={parameter.description || `Enter ${parameter.name}`}
            onChange={e => handleParameterChange(parameter.name, e.target.value)}
          />
        );
    }
  };

  const clearOutput = () => {
    setExecutionResponse(null);
  };

  if (!agent) {
    return (
      <Flex flexDirection="column" gap="16" p="16">
        <Text color="fg.secondary">No agent selected for execution.</Text>
      </Flex>
    );
  }

  return (
    <ArtifactProvider>
      <ArtifactContainer>
        <Flex flexDirection="column" gap="16" p="16">
          <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
            <Text fontWeight="600" fontSize="lg">
              Test Run: {agent.name}
            </Text>
            <Button
              icon={<IconXmark size="16" />}
              appearance="default"
              onClick={hideRightSidebar}
            />
          </Flex>
          {/* Input Disclosure */}
          <Card bg="bg.secondary" px="8" py="4">
            <Disclosure w="full" defaultOpen>
              <CardHeader>
                <DisclosureTrigger>
                  <Field label="Input Variables" />
                </DisclosureTrigger>
              </CardHeader>
              <DisclosureContent>
                <Flex flexDirection="column" gap="16" pt="8" pb="8" px="24">
                  {parameters.length > 0 ? (
                    <Flex flexDirection="column" gap="16">
                      <Flex flexDirection="row" gap="8" alignItems="center">
                        {isUploadingFile && (
                          <Flex alignItems="center" gap="8">
                            <Spinner size="sm" />
                            <Text fontSize="sm" color="fg.secondary">
                              Uploading files...
                            </Text>
                          </Flex>
                        )}
                      </Flex>

                      <Flex flexDirection="column" gap="16">
                        {parameters.map(parameter => (
                          <Field
                            key={parameter.name}
                            label={parameter.name}
                            required={parameter.required}
                          >
                            {renderParameterInput(parameter)}
                          </Field>
                        ))}
                      </Flex>

                      <Flex flexDirection="row" alignSelf="start">
                        <Tooltip
                          content={tooltipContentWithParams}
                          disabled={(() => {
                            const { isValid } = areRequiredParametersFilled();
                            return !isExecuting && !isUploadingFile && isValid;
                          })()}
                        >
                          <Button
                            appearance="primary"
                            icon={<IconPlay size="16" />}
                            onClick={handleExecuteAgent}
                            loading={isExecuting}
                            disabled={isRunAgentButtonDisabled}
                          >
                            Run
                          </Button>
                        </Tooltip>
                      </Flex>
                    </Flex>
                  ) : (
                    <Flex flexDirection="row" alignSelf="start">
                      <Tooltip
                        content={tooltipContentWithoutParams}
                        disabled={isRunAgentButtonDisabled}
                      >
                        <EmptyStateMessage
                          message="No variables defined for this agent."
                          action={{
                            label: 'Run',
                            onClick: handleExecuteAgent,
                            icon: <IconPlay size="16" />,
                            loading: isExecuting,
                            disabled: isRunAgentButtonDisabled,
                          }}
                        />
                      </Tooltip>
                    </Flex>
                  )}
                </Flex>
              </DisclosureContent>
            </Disclosure>
          </Card>

          {/* Artifact Disclosure */}
          {execution?.artifacts && execution.artifacts.length > 0 && (
            <Card bg="bg.secondary" px="8" py="4">
              <Disclosure w="full" defaultOpen>
                <CardHeader>
                  <DisclosureTrigger>
                    <Field label="Artifacts" info="Agent Execution Artifacts:" />
                  </DisclosureTrigger>
                </CardHeader>
                <DisclosureContent>
                  <Artifacts artifacts={execution?.artifacts} />
                </DisclosureContent>
              </Disclosure>
            </Card>
          )}
          {/* Output Disclosure */}
          <Card bg="bg.secondary" px="8" py="4">
            <Disclosure w="full" defaultOpen>
              <CardHeader>
                <DisclosureTrigger>
                  <Field label="Output" />
                </DisclosureTrigger>
              </CardHeader>
              <DisclosureContent>
                <Flex flexDirection="column" gap="16" pt="24" pb="20" px="24">
                  {execution ? (
                    <Flex flexDirection="column" gap="16">
                      <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
                        <Text fontSize="sm" color="fg.secondary">
                          Execution Results
                        </Text>
                        <Flex flexDirection="row" gap="8" alignItems="center">
                          {isStreaming && executionResponse?.id && (
                            <StopExecutionButton
                              executionId={executionResponse.id}
                              buttonSize="sm"
                            />
                          )}
                          <Button
                            size="sm"
                            appearance="subtle"
                            icon={<IconTrashCan size="14" />}
                            onClick={clearOutput}
                          >
                            Clear
                          </Button>
                        </Flex>
                      </Flex>

                      <Field label="Execution Details">
                        <ExecutionDetailsCard
                          executionId={execution.id}
                          status={execution.status}
                          variant="compact"
                        />
                        {(execution.status === 'COMPLETED' || execution.status === 'FAILED') &&
                          execution.result && (
                            <Box mt="16">
                              <ExecutionOutputDisplay
                                result={execution.result}
                                showStatus={false}
                              />
                            </Box>
                          )}
                      </Field>
                    </Flex>
                  ) : (
                    <EmptyStateMessage
                      message="No output available."
                      subMessage="Execute the agent to see results here."
                    />
                  )}
                </Flex>
              </DisclosureContent>
            </Disclosure>
          </Card>

          {/* Memory Chunks Disclosure */}
          {executionResponse?.links?.memory && (
            <Card bg="bg.secondary" px="8" py="4">
              <Disclosure w="full" defaultOpen>
                <CardHeader>
                  <DisclosureTrigger>
                    <Field label="Execution Memory" />
                  </DisclosureTrigger>
                </CardHeader>
                <DisclosureContent>
                  <Flex flexDirection="column" gap="16" pt="24" pb="20" px="24">
                    <MemoryChunksDisplay
                      memoryChunks={memoryChunks}
                      isStreaming={isStreaming}
                      emptyMessage="No memory chunks available."
                    />
                  </Flex>
                </DisclosureContent>
              </Disclosure>
            </Card>
          )}
        </Flex>
      </ArtifactContainer>
    </ArtifactProvider>
  );
}
